import React, { useState, useEffect, Fragment, useRef } from "react";
import { Row, Col, FormGroup, Tooltip, Label } from "reactstrap";
import { LoadingField, FormSelect, LoadingMessage, FormNumber, FormCheck } from "./../../Controls";
import Attachments from "./Attachments";
import { FormField } from "../../../..";
import classnames from "classnames";
import style from "./styles.module.scss";
import { WizzMode, Mode, AssetStatus, DPEditing, SinkType } from "../../types";
import { Field } from "../../..";
import { SuggestIssuer } from "./../../Controls2";
import Autosuggest from "../../../Autosuggest/Autosuggest";
import { Suggest } from "./../../Controls";

const General = ({
  mode,
  fields,
  previousFields,
  errors,
  entities,
  services,
  updateFiles,
  wizzOnChange,
  wizzToggle,
  decimalScale,
   role
}) => {
  const [dpEditable, setDPEditable] = useState(DPEditing.None);
  const [loaded, setLoaded] = useState(false);
  const [readonly, setReadonly] = useState(mode == WizzMode.View);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  const renderSuggestion = (suggestion) => (
    <div>
      {suggestion.number}
      {" - "}
      <span>{suggestion.name}</span>
    </div>
  );

  const getSuggestionValue = (suggestion) => `${suggestion.number} - ${suggestion.name}`;

  const onSuggestionsFetchRequested = ({ value }) =>
    services.fetchSearchAccount({ text: value }).then((response) =>
      response.response.accounts.map((a) => {
        const accountAttrs = JSON.parse(a.Value);

        return {
          id: a.Id,
          name: accountAttrs.value,
          number: accountAttrs.key,
        };
      })
    );

  const onChangeSuggest = (method, value) => {
    if (method != "type" && value.id) {
      wizzOnChange({ AssetUnderlyingPortfolio: +value.id });
    }
  };

  useEffect(() => {
    if (!loaded) {
      if (fields && entities.dataProviders) {
        let DPEditableState = DPEditing.None;
        setReadonly(mode == WizzMode.View);

        if (mode !== WizzMode.New) {
          const { AssetDefaultIdentifier: defaultId, AssetDefaultIdentifierValue: value } = fields;
          // if cloned, come with empty value
          if (!defaultId || defaultId === "0") {
            DPEditableState = DPEditing.Both;
          } else if (!value || value === "") {
            DPEditableState = DPEditing.OnlyValue;
          }
        } else {
          DPEditableState = DPEditing.Both;
        }

        setDPEditable(DPEditableState);

        let nextFields = { ...fields };
        if (mode == WizzMode.New) {
          // select default isin
          nextFields.AssetDefaultIdentifier = getDefaultAssetProvider(entities.dataProviders);
        }
        setLoaded(true);
        wizzOnChange(nextFields);
      }
    }
  }, [fields, entities]);

  const onChange = (name, value) => {
    wizzOnChange({ [name]: value });
  };

  const onChangeCountry = (name, value) => {
    wizzOnChange({ [name]: value.CountryId });
  };

  const onChangeIssuer = (name, value) => {
    wizzOnChange({ [name]: value.IssuerId });
  };

  const handleChangeFiles = (file) => {
    updateFiles(file.id, file);

    if (file.mode == Mode.Insert) {
      onChange(
        "Attachments",
        fields.Attachments.concat({
          AssetAttachmentId: file.id,
          AssetAttachmentName: file.name,
          AssetAttachmentExtension: file.extension,
        })
      );
    } else if (file.mode == Mode.Delete) {
      onChange(
        "Attachments",
        fields.Attachments.filter((a) => a.AssetAttachmentId != file.id)
      );
    }
  };

  const { subAssetClasses, dataProviders, riskTypes, creditRatings, issuers } = entities;

  const lastInferredRef = useRef({ issuerId: null, riskTypeId: null, agencyId: null, score: null });
  const manualOverrideRef = useRef({ risk: false, rating: false });

  useEffect(() => {
    if (mode !== WizzMode.New) return;
    if (!fields || !fields.IssuerId) return;

    try {
      let issuerData = null;
      if (Array.isArray(issuers) && issuers.length) {
        issuerData = issuers.find(i => String(i.IssuerId) === String(fields.IssuerId));
      }
      if (!issuerData && Array.isArray(subAssetClasses)) {
        issuerData = subAssetClasses.find(sc => String(sc.IssuerId) === String(fields.IssuerId));
      }
      if (!issuerData) return;

      const issuerChanged = lastInferredRef.current.issuerId && String(lastInferredRef.current.issuerId) !== String(fields.IssuerId);

      const updates = {};
      let changed = false;

      const issuerRiskId = issuerData.IssuerRiskId;
      const currentRisk = fields.AssetRiskTypeId;
      const riskEmpty = !currentRisk;
      if (issuerRiskId) {
        if (((issuerChanged && !manualOverrideRef.current.risk)) && String(currentRisk) !== String(issuerRiskId)) {
          updates.AssetRiskTypeId = issuerRiskId;
          updates.RiskSync = true;
          changed = true;
          lastInferredRef.current.riskTypeId = issuerRiskId;
        }
      }

      const issuerAgency = issuerData.IssuerCreditRatingAgencyId;
      const issuerScore = issuerData.IssuerCreditRatingAgencyScore;

      const agencyEmpty = !fields.CreditRatingAgencyID || String(fields.CreditRatingAgencyID) === '0';
      const scoreEmpty = !fields.CreditRatingAgencyScore;

      if (issuerAgency) {
        if ((agencyEmpty || (issuerChanged && !manualOverrideRef.current.rating)) && String(fields.CreditRatingAgencyID) !== String(issuerAgency)) {
          updates.CreditRatingAgencyID = issuerAgency;
          const matchAgency = Array.isArray(creditRatings)
            ? creditRatings.find(r => String(r.CreditRatingAgencyId) === String(issuerAgency))
            : null;
          if (matchAgency) {
            updates.CreditRatingAgencyName = matchAgency.CreditRatingAgencyName;
          }
          changed = true;
          lastInferredRef.current.agencyId = issuerAgency;
        }
      }
      if (issuerScore) {
        if ((scoreEmpty || (issuerChanged && !manualOverrideRef.current.rating)) && String(fields.CreditRatingAgencyScore) !== String(issuerScore)) {
          updates.CreditRatingAgencyScore = issuerScore;
          changed = true;
          lastInferredRef.current.score = issuerScore;
        }
      }

      if (changed) {
        lastInferredRef.current.issuerId = fields.IssuerId;
        wizzOnChange(updates);
      } else if (issuerChanged) {
        lastInferredRef.current.issuerId = fields.IssuerId;
      }
    } catch (err) {
      console.debug('[Inference] Failed to infer issuer risk/rating', err);
    }
  }, [fields.IssuerId, mode, issuers, subAssetClasses, creditRatings, fields.AssetRiskTypeId, fields.CreditRatingAgencyID, fields.CreditRatingAgencyScore]);

  const handleRiskChange = (name, value, _label) => {
    if (lastInferredRef.current.riskTypeId && String(value) !== String(lastInferredRef.current.riskTypeId)) {
      manualOverrideRef.current.risk = true;
    }
    wizzOnChange({ [name]: value });
  };

  const handleAgencyChange = (name, value) => {
    if (lastInferredRef.current.agencyId && String(value) !== String(lastInferredRef.current.agencyId)) {
      manualOverrideRef.current.rating = true;
    }
    const selected = agencyOptions.find((a) => String(a.id) === String(value));
    wizzOnChange({
      CreditRatingAgencyID: value,
      CreditRatingAgencyName: selected ? selected.name : '',
      CreditRatingAgencyScore: ''
    });
  };

  const handleScoreChange = (name, value) => {
    if (lastInferredRef.current.score && String(value) !== String(lastInferredRef.current.score)) {
      manualOverrideRef.current.rating = true;
    }
    wizzOnChange({ [name]: value });
  };

  const showRiskInferred = (
    lastInferredRef.current.issuerId &&
    String(lastInferredRef.current.issuerId) === String(fields.IssuerId) &&
    lastInferredRef.current.riskTypeId &&
    String(lastInferredRef.current.riskTypeId) === String(fields.AssetRiskTypeId) &&
    !manualOverrideRef.current.risk
  );

  const showRatingInferred = (
    lastInferredRef.current.issuerId &&
    String(lastInferredRef.current.issuerId) === String(fields.IssuerId) &&
    (lastInferredRef.current.agencyId || lastInferredRef.current.score) &&
    (!manualOverrideRef.current.rating) &&
    (
      (lastInferredRef.current.agencyId && String(lastInferredRef.current.agencyId) === String(fields.CreditRatingAgencyID || '')) ||
      (lastInferredRef.current.score && String(lastInferredRef.current.score) === String(fields.CreditRatingAgencyScore || ''))
    )
  );

  const agencyOptions = creditRatings
    ? Array.from(
        new Map(
          creditRatings.map((r) => [String(r.CreditRatingAgencyId), String(r.CreditRatingAgencyName)])
        )
      ).map(([id, name]) => ({ id, name }))
    : [];

  const hasAgency = fields.CreditRatingAgencyID && String(fields.CreditRatingAgencyID) !== "0";

  const scoreOptions = creditRatings && hasAgency
    ? Array.from(
        new Set(
          creditRatings
            .filter((r) => String(r.CreditRatingAgencyId) == String(fields.CreditRatingAgencyID))
            .map((r) => String(r.CreditRatingScore))
        )
      ).map((score) => ({ id: score, name: score }))
    : [];

  const SinkTypeValues = Object.entries(SinkType).map(([key, value]) => ({
    id: key,
    description: value,
  }));

  return (
    <div className={classnames({ [style.center]: !fields })}>
      {fields ? (
        <Fragment>
          <Row>
            <Col lg="4">
              {subAssetClasses ? (
                <FormSelect
                  name="SubAssetClassId"
                  label="Sub Asset Class"
                  selectItems={subAssetClasses}
                  getSelectItemId={(i) => i.SubAssetClassId}
                  getSelectItemName={(i) => i.SubAssetClassName}
                  value={fields.SubAssetClassId}
                  error={errors.SubAssetClassId}
                  disabled={readonly || fields.AssetHasOrdersInProgress}
                  description={
                    !readonly && fields.AssetHasOrdersInProgress
                      ? "Cannot be changed because it has orders in progress"
                      : null
                  }
                  changedColor={fields.SubAssetClassId != previousFields.SubAssetClassId}
                  onChange={onChange}
                />
              ) : (
                <LoadingField label="Sub Asset Class" />
              )}
            </Col>
            <Col lg="2">
              {mode != WizzMode.New && (
                <Fragment>
                  <FormGroup className="mt-2 mb-1">
                    <FormCheck
                      className="text-nowrap"
                      name="AssetCanBeTradedFromWebClient"
                      label="Can be traded from web client"
                      value={fields.AssetCanBeTradedFromWebClient}
                      onChange={(e) => onChange("AssetCanBeTradedFromWebClient", e.target.checked)}
                      disabled={readonly}
                      changedColor={
                        fields.AssetCanBeTradedFromWebClient != previousFields.AssetCanBeTradedFromWebClient
                      }
                    />
                  </FormGroup>
                  <FormGroup className="mt-0 pt-1">
                    <Row>
                      <Col>
                        <FormCheck
                          name="AssetReadyToTrade"
                          label="Is ready to trade"
                          value={fields.AssetReadyToTrade}
                          onChange={(e) => onChange("AssetReadyToTrade", e.target.checked)}
                          disabled={
                            readonly ||
                            fields.Taxes.length == 0 ||
                            fields.AssetTicker == "" ||
                            fields.AssetTicker == "Not defined"
                          }
                          changedColor={fields.AssetReadyToTrade != previousFields.AssetReadyToTrade}
                        />
                      </Col>
                      {(fields.Taxes.length == 0 ||
                        fields.AssetTicker == "" ||
                        fields.AssetTicker == "Not defined") && (
                        <Col xs="auto">
                          <span style={{ fontSize: "1.5em", color: "#1E92F4" }}>
                            <i className="fa fa-info-circle" id="TooltipReadToTrade"></i>
                          </span>
                          <Tooltip
                            placement="right"
                            isOpen={tooltipOpen}
                            target="TooltipReadToTrade"
                            toggle={toggleTooltip}
                          >
                            {(fields.AssetTicker == "" || fields.AssetTicker == "Not defined") &&
                              "Assets must have a Ticker defined to be ready to Trade"}
                            {fields.Taxes.length == 0 &&
                              "Assets must have at least one Tax defined to be ready to Trade"}
                          </Tooltip>
                        </Col>
                      )}
                    </Row>
                  </FormGroup>
                </Fragment>
              )}
            </Col>
            <Col lg="2">
              {mode != WizzMode.New && (
                <Fragment>
                  <FormGroup className="mt-2 mb-1">
                    <FormCheck
                      name="AssetMaintainsFaceOnSink"
                      label="Asset Maintains Face On Sink"
                      value={fields.AssetMaintainsFaceOnSink}
                      onChange={(e) => onChange("AssetMaintainsFaceOnSink", e.target.checked)}
                      disabled={readonly}
                      changedColor={
                        fields.AssetMaintainsFaceOnSink != previousFields.AssetMaintainsFaceOnSink
                      }
                    />
                  </FormGroup>
                  <FormGroup className="mt-0 pt-1">
                    <Row>
                      <Col>
                        <FormCheck
                          name="AssetReadyToBook"
                          label="Is ready to book"
                          value={fields.AssetReadyToBook}
                          onChange={(e) => onChange("AssetReadyToBook", e.target.checked)}
                          disabled={
                            readonly ||
                            fields.Taxes.length == 0 ||
                            fields.AssetTicker == "" ||
                            fields.AssetTicker == "Not defined"
                          }
                          changedColor={fields.AssetReadyToBook != previousFields.AssetReadyToBook}
                        />
                      </Col>
                      {(fields.Taxes.length == 0 ||
                        fields.AssetTicker == "" ||
                        fields.AssetTicker == "Not defined") && (
                        <Col xs="auto">
                          <span style={{ fontSize: "1.5em", color: "#1E92F4" }}>
                            <i className="fa fa-info-circle" id="TooltipExample"></i>
                          </span>
                          <Tooltip
                            placement="right"
                            isOpen={tooltipOpen}
                            target="TooltipExample"
                            toggle={toggleTooltip}
                          >
                            {(fields.AssetTicker == "" || fields.AssetTicker == "Not defined") &&
                              "Assets must have a Ticker defined to be ready to Book"}
                            {fields.Taxes.length == 0 &&
                              "Assets must have at least one Tax defined to be ready to Book"}
                          </Tooltip>
                        </Col>
                      )}
                    </Row>
                  </FormGroup>
                </Fragment>
              )}
            </Col>
            <Col lg="2">
              <Fragment>
                <FormGroup className="mt-2 mb-1">
                  <FormCheck
                    name="AssetHasUnderlyingPortfolio"
                    label="Structured Asset"
                    value={fields.AssetHasUnderlyingPortfolio}
                    onChange={(e) => onChange("AssetHasUnderlyingPortfolio", e.target.checked)}
                    disabled={readonly}
                    changedColor={
                      fields.AssetHasUnderlyingPortfolio != previousFields.AssetHasUnderlyingPortfolio
                    }
                  />
                </FormGroup>
                <FormGroup className="mt-2 mb-1">
                  {fields.AssetHasUnderlyingPortfolio && (
                    <Fragment>
                      <Label>Asset Structure Account</Label>
                      <Autosuggest
                        renderSuggestion={renderSuggestion}
                        getSuggestionValue={getSuggestionValue}
                        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                        value={fields.AssetUnderlyingPortfolio ? fields.AssetUnderlyingPortfolio : ""}
                        inputProps={{
                          placeholder: "",
                          invalid: !!errors.AssetUnderlyingPortfolio,
                          disabled: readonly,
                        }}
                        onChange={onChangeSuggest}
                      />
                      <Error error={errors.AssetUnderlyingPortfolio} />
                    </Fragment>
                  )}
                </FormGroup>
              </Fragment>
            </Col>
            {fields.AssetCanUseMTM && (
              <Col lg="2">
                {mode != WizzMode.New && (
                  <FormGroup className="mt-2 mb-1">
                    <FormCheck
                      name="AssetHasMTM"
                      label="Asset Marked to Market"
                      value={fields.AssetHasMTM}
                      onChange={(e) => onChange("AssetHasMTM", e.target.checked)}
                      disabled={readonly}
                      changedColor={fields.AssetHasMTM != previousFields.AssetHasMTM}
                    />
                  </FormGroup>
                )}
              </Col>
            )}
          </Row>
          <Row>
            <Col lg="3">
              <FormSelect
                name="AssetSinktype"
                label="Sink Type"
                selectItems={SinkTypeValues}
                getSelectItemId={(i) => i.id}
                getSelectItemName={(i) => i.description}
                value={fields.AssetSinktype || ""}
                disabled={readonly}
                onChange={onChange}
                changedColor={fields.AssetSinktype != previousFields.AssetSinktype}
              />
            </Col>
            <Col lg="4">
              {subAssetClasses ? (
                <FormSelect
                  name="AssetDefaultIdentifier"
                  label="Data provider"
                  selectItems={dataProviders}
                  getSelectItemId={(i) => i.DataProviderId}
                  getSelectItemName={(i) => i.DataProviderName}
                  value={fields.AssetDefaultIdentifier === "0" ? undefined : fields.AssetDefaultIdentifier}
                  error={errors.AssetDefaultIdentifier}
                  disabled={readonly || dpEditable !== DPEditing.Both}
                  onChange={onChange}
                  changedColor={fields.AssetDefaultIdentifier != previousFields.AssetDefaultIdentifier}
                />
              ) : (
                <LoadingField label="Data provider" />
              )}
            </Col>
            <Col lg="4">
              {fields.AssetDefaultIdentifier && dataProviders && (
                <FormField
                  name="AssetDefaultIdentifierValue"
                  label={getDataProviderLabel(fields, dataProviders)}
                  value={fields.AssetDefaultIdentifierValue}
                  disabled={readonly || dpEditable === DPEditing.None}
                  onChange={onChange}
                  changedColor={
                    fields.AssetDefaultIdentifierValue != previousFields.AssetDefaultIdentifierValue
                  }
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col lg="4">
              <FormField
                name="AssetTicker"
                label="Asset Ticker"
                value={fields.AssetTicker}
                error={errors.AssetTicker}
                disabled={readonly}
                onChange={onChange}
                changedColor={fields.AssetTicker != previousFields.AssetTicker}
              />
              {entities.currencies ? (
                <FormSelect
                  name="AssetSettlementCurrencyId"
                  label="Default settlement currency"
                  selectItems={entities.currencies}
                  getSelectItemId={(i) => i.currencyId}
                  getSelectItemName={(i) => `${i.currencyISO} - ${i.currencyDescription}`}
                  value={fields.AssetSettlementCurrencyId}
                  error={errors.AssetSettlementCurrencyId}
                  disabled={mode !== WizzMode.New}
                  onChange={onChange}
                />
              ) : (
                <LoadingField label="Default settlement currency" />
              )}
              {riskTypes ? (
                <FormSelect
                  name="AssetRiskTypeId"
                  label="Asset Risk type"
                  selectItems={riskTypes}
                  getSelectItemId={(i) => i.AssetRiskTypeId}
                  getSelectItemName={(i) => i.AssetRiskTypeDesc}
                  value={fields.AssetRiskTypeId}
                  error={errors.AssetRiskTypeId}
                  disabled={readonly}
                  onChange={handleRiskChange}
                  description={showRiskInferred ? 'Inferred from issuer' : null}
                  changedColor={fields.AssetRiskTypeId != previousFields.AssetRiskTypeId}
                />
              ) : (
                <LoadingField label="Asset Risk type" />
              )}

               {agencyOptions.length > 0 && (
                <div className="d-flex flex-wrap align-items-start mt-2">
                  <div style={{ minWidth: 180, marginRight: 8 }}>
                    <FormSelect
                      name="CreditRatingAgencyID"
                      label="Agency"
                      selectItems={agencyOptions}
                      getSelectItemId={(i) => i.id}
                      getSelectItemName={(i) => i.name}
                      value={
                        fields.CreditRatingAgencyID === "0" || fields.CreditRatingAgencyID === 0 || fields.CreditRatingAgencyID === ""
                          ? undefined
                          : fields.CreditRatingAgencyID
                      }
                      disabled={readonly|| role!="ops"}
                      onChange={handleAgencyChange}
                      description={showRatingInferred ? 'Inferred from issuer' : null}
                      changedColor={fields.CreditRatingAgencyID != previousFields.CreditRatingAgencyID}
                    />
                  </div>
                  <div style={{ minWidth: 180 }}>
                    <FormSelect
                      name="CreditRatingAgencyScore"
                      label="Score"
                      selectItems={scoreOptions}
                      getSelectItemId={(i) => i.id}
                      getSelectItemName={(i) => i.name}
                      value={fields.CreditRatingAgencyScore ? fields.CreditRatingAgencyScore : undefined}
                      disabled={readonly || !hasAgency||role!="ops"}
                      onChange={handleScoreChange}
                      description={showRatingInferred && !fields.CreditRatingAgencyScore ? 'Inferred from issuer' : null}
                      changedColor={fields.CreditRatingAgencyScore != previousFields.CreditRatingAgencyScore}
                    />
                  </div>
                </div>
              )}
              {(fields.RiskSync === true || fields.RiskSync === false) && (
                <div className="mt-1">
                  <span className={`badge badge-${fields.RiskSync ? 'success' : 'warning'}`}>
                    <i className={`fa ${fields.RiskSync ? 'fa-check' : 'fa-exclamation-triangle'} mr-1`} />
                    {fields.RiskSync ? 'Risks are synced with the issuer' : 'Risks are not synced with the issuer'}
                  </span>
                </div>
              )}
              <Attachments
                id={fields.AssetId}
                readonly={readonly}
                attachments={fields.Attachments}
                services={services}
                update={handleChangeFiles}
              />
            </Col>
            <Col lg="4">
              <FormField
                name="AssetName"
                label="Asset Name"
                value={fields.AssetName}
                error={errors.AssetName}
                disabled={readonly}
                onChange={onChange}
                changedColor={fields.AssetName != previousFields.AssetName}
              />

              {mode == WizzMode.New && (
                <FormNumber
                  name="AssetLast"
                  label="Asset Price"
                  value={fields.AssetLast}
                  disabled={readonly}
                  onChange={onChange}
                  changedColor={fields.AssetLast != previousFields.AssetLast}
                  decimalScale={decimalScale}
                  dataTypeDecimalName="price"
                />
              )}
              <Suggest
                name="CountryId"
                label="Country"
                fieldId="CountryId"
                fieldName="CountryName"
                fieldExtension="CountryISO2"
                valueId={fields.CountryId}
                valueName={fields.CountryName}
                getSelectItemId={(i) => i.Id}
                getSelectItemName={(i) => i.Name}
                getSelectItemExtension={(i) => i.Code}
                service={services.searchCountry}
                disabled={readonly}
                error={errors.CountryId}
                onChange={onChangeCountry}
                changedColor={fields.CountryId != previousFields.CountryId}
              />
              <SuggestIssuer
                name="IssuerId"
                label="Issuer"
                fieldId="IssuerId"
                fieldName="IssuerName"
                valueId={fields.IssuerId}
                valueName={fields.IssuerName}
                getSelectItemId={(i) => i.Id}
                getSelectItemName={(i) => i.Value}
                error={errors.IssuerId}
                services={services}
                serviceCreateIssuer={services.createIssuer}
                disabled={readonly}
                onChange={onChangeIssuer}
                changedColor={fields.IssuerId != previousFields.IssuerId}
              />
            </Col>
          </Row>
        </Fragment>
      ) : (
        <LoadingMessage text="Loading asset general info" />
      )}
    </div>
  );
};

const Error = ({ error }) =>
  !!error ? (
    <div className="invalid-feedback" style={{ display: "block" }}>
      {error}
    </div>
  ) : null;

const getDataProviderLabel = (fields, providers) => {
  let label;
  if (providers) {
    const provider = providers.find((prov) => prov.DataProviderId == fields.AssetDefaultIdentifier);

    if (provider) {
      label = provider.DataProviderInternalName;
    }
  }

  if (!label) {
    label = "Not defined";
  }

  return label;
};

function getDefaultAssetProvider(providers) {
  let candidate = providers.find((p) => p.DataProviderName == "ISIN");

  if (candidate) {
    return candidate.DataProviderId;
  }

  return null;
}

export function validateGeneral(fields) {
  const fieldNames = [
    "SubAssetClassId",
    "AssetDefaultIdentifier",
    "AssetSettlementCurrencyId",
    "AssetTicker",
    "AssetName",
    "CountryId",
    "IssuerId",
    "AssetRiskTypeId",
  ];
  const errors = {};

  fieldNames.forEach((name) => {
    if (fields[name] == undefined || fields[name] == "") {
      errors[name] = "Required field";
    }
  });

  return errors;
}

export default General;
